import React, { useState } from 'react';
import NewProjectWizard from './NewProjectWizard.jsx';
import ProjectSwitcher from './ProjectSwitcher.jsx';
import { useDeal } from '../deals/DealContext.jsx';
import '../meeting/meeting.css';

/**
 * Two-level shell (meeting path redesign):
 *   Workspace — Deals · Investors · Admin (across deals)
 *   Deal      — five steps in the order a buyer works a package.
 *   Legacy reference dashboards (BACKUP_DASHBOARDS) are hidden from all nav but still
 *   open by direct link (#reserves, #cashflow, #map, #abs/summary, …) and inline step links.
 */
export const MEETING_STEPS = [
  { key: 'analysis', n: 1, label: 'Analysis' },
  { key: 'forecast', n: 2, label: 'Forecast' },
  { key: 'value', n: 3, label: 'Value' },
  { key: 'finance', n: 4, label: 'Finance' },
  { key: 'package', n: 5, label: 'Package' },
];

export const BACKUP_DASHBOARDS = [
  { key: 'reserves', label: 'Reserves' },
  { key: 'cashflow', label: 'Cash flow & LOS' },
  { key: 'wells', label: 'Wells' },
  { key: 'map', label: 'Map' },
  { key: 'land', label: 'Land & midstream' },
  { key: 'geoscience', label: 'Geoscience' },
  { key: 'diligence', label: 'VDR status & contracts' },
  { key: 'executive', label: 'Executive summary' },
  { key: 'decision', label: 'Bid desk (full)' },
  { key: 'sensitivity', label: 'Model & sensitivity' },
  { key: 'assets', label: 'Decline workbench' },
  { key: 'abs', label: 'ABS desk (full)' },
];

/** Flat list for label lookups. */
export const DEAL_TOOLS = [
  { key: 'checklist', label: 'Checklist' },
  { key: 'dataroom', label: 'Data room' },
];

export const DASHBOARDS = [
  ...MEETING_STEPS,
  ...DEAL_TOOLS,
  ...BACKUP_DASHBOARDS,
  { key: 'overview', label: 'Analysis' },
  { key: 'verdict', label: 'Analysis' },
  { key: 'deals', label: 'Deals' },
  { key: 'screen', label: 'Quick screen' },
];

function dealLine(deal, packageMeta) {
  const parts = [];
  const seller = packageMeta?.seller || deal?.seller;
  if (seller) parts.push(String(seller).replace(/\s*\(.*\)\s*$/, ''));
  if (deal?.subtitle) parts.push(deal.subtitle);
  return parts.join(' · ');
}

function bidLine(packageMeta, deal) {
  const due = packageMeta?.bidsDue;
  if (!due) return deal?.bidsDueLabel ? `Bids due ${deal.bidsDueLabel}` : null;
  const d = new Date(`${due}T17:00:00Z`);
  const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const past = d.getTime() < Date.now();
  const eff = packageMeta?.effectiveDate
    ? ` · effective ${new Date(`${packageMeta.effectiveDate}T12:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })}`
    : '';
  return `${past ? 'Bids were due' : 'Bids due'} ${label}${eff}`;
}

export default function Layout({
  activeKey,
  onNavigate,
  children,
  user,
  isAdmin,
  isInternal,
  onLogout,
  workspace = 'deal',
  packageMeta = null,
  deal = null,
  currentPath = null,
}) {
  const { refreshDrafts } = useDeal();
  const [wizardOpen, setWizardOpen] = useState(false);
  const inDeal = workspace === 'deal';
  const backupActive = BACKUP_DASHBOARDS.some((d) => d.key === activeKey) || activeKey === 'well';
  const asOf = packageMeta?.priceDeck || packageMeta?.generatedAt || null;

  return (
    <div className="shell">
      <header className="shell-top">
        <div className="shell-top-left">
          <button type="button" className="shell-brand" onClick={() => onNavigate('deals')}>
            D2D <span>Energy</span>
          </button>
          <nav className="shell-ws" aria-label="Workspace">
            <button
              type="button"
              className={`shell-ws-btn ${workspace === 'deals' || inDeal ? 'active' : ''}`}
              onClick={() => onNavigate('deals')}
            >
              Deals
            </button>
            {isInternal && (
              <button
                type="button"
                className={`shell-ws-btn ${workspace === 'investors' ? 'active' : ''}`}
                onClick={() => onNavigate('crm')}
              >
                Investors
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className={`shell-ws-btn ${workspace === 'admin' ? 'active' : ''}`}
                onClick={() => onNavigate('admin')}
              >
                Admin
              </button>
            )}
          </nav>
        </div>
        <div className="shell-top-right">
          {isAdmin && workspace === 'deals' ? (
            <button type="button" className="shell-signout" onClick={() => setWizardOpen(true)}>
              New deal
            </button>
          ) : null}
          {user && <span className="shell-user">{user.name}</span>}
          {user && (
            <button type="button" className="shell-signout" onClick={onLogout}>
              Sign out
            </button>
          )}
        </div>
      </header>

      {inDeal && deal ? (
        <div className="shell-deal">
          <div className="shell-deal-titles">
            <button type="button" className="shell-back" onClick={() => onNavigate('deals')}>
              ← All deals
            </button>
            <div className="shell-deal-name">
              <ProjectSwitcher
                currentPath={currentPath || activeKey}
                onOpenAllProjects={() => onNavigate('deals')}
                onCreateProject={isAdmin ? () => setWizardOpen(true) : undefined}
              />
              <h1>{deal.label}</h1>
              <span>{dealLine(deal, packageMeta)}</span>
            </div>
          </div>
          <div className="shell-deal-meta">
            {bidLine(packageMeta, deal) ? <span>{bidLine(packageMeta, deal)}</span> : null}
            <span className="shell-chip">Confidential · CA</span>
          </div>
        </div>
      ) : null}

      <div className="shell-body">
        {inDeal ? (
          <nav className="shell-rail" aria-label="Deal steps">
            {MEETING_STEPS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`shell-step ${activeKey === s.key ? 'active' : ''}`}
                aria-current={activeKey === s.key ? 'page' : undefined}
                onClick={() => onNavigate(s.key)}
              >
                <span className="n">{s.n}</span>
                {s.label}
              </button>
            ))}
            <div className="shell-rail-split" />
            <div className="shell-rail-label">Diligence</div>
            {DEAL_TOOLS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`shell-step ${activeKey === s.key ? 'active' : ''}`}
                aria-current={activeKey === s.key ? 'page' : undefined}
                onClick={() => onNavigate(s.key)}
              >
                <span className="n">·</span>
                {s.label}
              </button>
            ))}
          </nav>
        ) : null}
        <main className={`shell-main ${inDeal ? '' : 'wide'} ${backupActive ? 'legacy' : ''}`}>
          {children}
        </main>
      </div>

      <footer className="shell-foot">
        <span>
          <b>Data2Dollars Energy</b>
          {inDeal && deal ? ` · ${deal.label}` : ''}
        </span>
        {inDeal && asOf ? <span>Price deck: {asOf}</span> : null}
        <span>Confidential under CA · Prepared by Data2Dollars.ai</span>
      </footer>

      {isAdmin ? (
        <NewProjectWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onSaved={() => {
            refreshDrafts?.();
            setWizardOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
